import { MediaObject } from 'src/api/services/MediaClient/MediaClient.types';

/**
 * The number of days after which a restored media is considered expired
 * and should allow reinitializing unarchiving
 */
export const RESTORATION_EXPIRY_DAYS = 14;

/**
 * Checks if the provided media's restoration expiry date is older than the threshold
 * @param media The MediaObject to check
 * @returns boolean - true if the media's restoration has expired (more than 14 days old)
 */
export const hasRestorationExpired = (media?: MediaObject): boolean => {
  if (!media?.restoration_expiry) {
    return false;
  }

  try {
    const expiryDateObj = new Date(media.restoration_expiry);
    const today = new Date();

    // Calculate the difference in milliseconds
    const diffTime = today.getTime() - expiryDateObj.getTime();

    // Convert milliseconds to days (1000ms * 60s * 60min * 24h)
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > RESTORATION_EXPIRY_DAYS;
  } catch (error) {
    console.error('Error calculating restoration expiry:', error);
    return false; // In case of error, default to not expired
  }
};

/**
 * Determines if a media is ready for download (restoration completed and not expired)
 * @param media The MediaObject to check
 * @returns boolean - true if the media is ready for download
 */
export const isMediaReadyForDownload = (media?: MediaObject): boolean => {
  if (!media) {
    return false;
  }

  return media.restoration_status === 'complete' && !hasRestorationExpired(media);
};

/**
 * Determines if a media is currently being unarchived
 * @param media The MediaObject to check
 * @returns boolean - true if the media is in the process of being unarchived
 */
export const isMediaBeingUnarchived = (media?: MediaObject): boolean => {
  if (!media) {
    return false;
  }

  return !!media.restoration_initialized_at && media.restoration_status !== 'complete';
};

/**
 * Determines if a media needs unarchiving, either because it was never unarchived
 * or because its restoration has expired
 * @param media The MediaObject to check
 * @param videoIsArchived Boolean indicating if the video is archived
 * @returns boolean - true if the media needs to be unarchived
 */
export const isMediaNeedsUnarchiving = (media?: MediaObject, videoIsArchived = false): boolean => {
  if (!media) {
    return false;
  }

  const isExpiredRestoration =
    media.restoration_status === 'complete' && hasRestorationExpired(media);

  return videoIsArchived || isExpiredRestoration;
};
